/**
 * Safety Intelligence Page
 * Comprehensive safety tools dashboard
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  IncidentTimelinePanel,
  PropertySafetyScoreCard,
  RouteSafetyPlannerCard,
  CommunityReportsPanel
} from '@/components/safety';
import {
  Shield,
  TrendingUp,
  Home,
  Route,
  Users,
  ArrowLeft,
  Radio,
  ChevronDown,
  User,
  LogOut,
  Crown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const SafetyIntelligencePage = () => {
  const { user, logout } = useAuth();
  const { getTierInfo } = useSubscription();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('timeline');

  const tierInfo = getTierInfo();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const tabs = [
    { id: 'timeline', label: 'Live Timeline', icon: TrendingUp, color: 'text-teal-400' },
    { id: 'property', label: 'Property Score', icon: Home, color: 'text-blue-400' },
    { id: 'route', label: 'Route Safety', icon: Route, color: 'text-amber-400' },
    { id: 'community', label: 'Community Reports', icon: Users, color: 'text-purple-400' }
  ];

  return (
    <div className="min-h-screen bg-slate-950" data-testid="safety-intelligence-page">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left side */}
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to OverWatch
              </Button>
              
              <div className="h-6 w-px bg-slate-700" />
              
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-400" />
                <h1 className="text-lg font-semibold text-white">Safety Intelligence</h1>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-teal-400"
              >
                <Radio className="w-4 h-4 mr-2" />
                Scanners
              </Button>

              {/* User menu */}
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 text-slate-300 hover:text-slate-100"
                    >
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center">
                        <span className="text-xs font-bold text-white">
                          {user.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="hidden sm:inline text-sm">{user.name?.split(' ')[0]}</span>
                      <Badge className={`${tierInfo.color === 'teal' ? 'bg-teal-600' : 'bg-amber-600'} text-white text-[10px] px-1.5`}>
                        {tierInfo.displayName}
                      </Badge>
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 glass-card border-slate-800">
                    <DropdownMenuLabel className="text-slate-400 font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium text-slate-200">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-slate-800" />
                    <DropdownMenuItem 
                      className="cursor-pointer hover:bg-slate-800"
                      onClick={() => navigate('/profile')}
                    >
                      <User className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="cursor-pointer hover:bg-slate-800"
                      onClick={() => navigate('/pricing')}
                    >
                      <Crown className="mr-2 h-4 w-4 text-amber-500" />
                      <span>Upgrade</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-800" />
                    <DropdownMenuItem 
                      className="cursor-pointer hover:bg-slate-800 text-red-400"
                      onClick={handleLogout}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Log Out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 gap-2 bg-slate-900/50 p-1 rounded-lg mb-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="flex items-center gap-2 data-[state=active]:bg-slate-800 data-[state=active]:text-white"
                >
                  <Icon className={`w-4 h-4 ${activeTab === tab.id ? tab.color : 'text-slate-500'}`} />
                  <span className="hidden sm:inline">{tab.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Tab Content */}
          <div className="bg-slate-900/30 border border-slate-800 rounded-xl overflow-hidden min-h-[calc(100vh-200px)]">
            <TabsContent value="timeline" className="m-0 h-[calc(100vh-200px)]">
              <IncidentTimelinePanel />
            </TabsContent>
            
            <TabsContent value="property" className="m-0 h-[calc(100vh-200px)]">
              <PropertySafetyScoreCard />
            </TabsContent>
            
            <TabsContent value="route" className="m-0 h-[calc(100vh-200px)]">
              <RouteSafetyPlannerCard />
            </TabsContent>
            
            <TabsContent value="community" className="m-0 h-[calc(100vh-200px)]">
              <CommunityReportsPanel />
            </TabsContent>
          </div>
        </Tabs>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const descriptions = {
              timeline: 'Real-time feed of all safety incidents from scanners, weather, and community reports',
              property: 'Get comprehensive 0-100 safety scores for any address or location',
              route: 'Analyze safety along travel routes with danger zone highlighting',
              community: 'Submit and view crowdsourced safety reports from your community'
            };
            return (
              <div
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  activeTab === tab.id
                    ? 'border-teal-500/50 bg-teal-500/10'
                    : 'border-slate-700 bg-slate-800/30 hover:bg-slate-800/50'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg ${
                  activeTab === tab.id ? 'bg-teal-500/20' : 'bg-slate-700/50'
                } flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 ${activeTab === tab.id ? tab.color : 'text-slate-500'}`} />
                </div>
                <h3 className="text-sm font-medium text-white mb-1">{tab.label}</h3>
                <p className="text-xs text-slate-400">{descriptions[tab.id]}</p>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default SafetyIntelligencePage;
